import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from '../../config/api';
import { API_URL } from '../../config/api';
import { MdLabel, MdAdd, MdClose, MdPushPin, MdAutoAwesome, MdSearch } from 'react-icons/md';
import { toast } from 'sonner';

/**
 * Tags tab on the Favorites page.
 *
 * Two stacked sections:
 *   1. "From your favorited polls" (read-only chips, with a Pin button on each).
 *   2. "Added by you" (manual chips with × remove).
 *
 * The Add control is an autocomplete search of the global `tags` table with
 * a free-text "Create new tag" fallback for novel interests.
 */
export default function FavoriteTagsTab() {
  const [loading, setLoading] = useState(true);
  const [inferred, setInferred] = useState([]);
  const [manual, setManual] = useState([]);
  const [busyTagId, setBusyTagId] = useState(null);

  // Add-tag UI state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const manualIdSet = useMemo(() => new Set(manual.map(t => t.tag_id)), [manual]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/favorites/tags`);
      setInferred(res.data?.inferred || []);
      setManual(res.data?.manual || []);
    } catch (err) {
      console.error('Failed to load favorite tags:', err);
      toast.error('Failed to load favorite tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Search-as-you-type with debounce
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await axios.get(`${API_URL}/api/tags/search`, {
          params: { q: query.trim(), limit: 8 },
        });
        setResults(res.data?.results || []);
      } catch (err) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pinTag = async (tag_id, name) => {
    setBusyTagId(tag_id);
    try {
      await axios.post(`${API_URL}/api/favorites/tags`, { tag_id });
      toast.success(`Pinned "${name}" to your interests`);
      setQuery('');
      setResults([]);
      setShowResults(false);
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to pin tag';
      toast.error(msg.includes('user_favorite_tags') ? 'Tags storage not yet initialized — apply the migration in Supabase.' : msg);
    } finally {
      setBusyTagId(null);
    }
  };

  const createAndPin = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setBusyTagId('__creating__');
    try {
      const res = await axios.post(`${API_URL}/api/favorites/tags/create`, { name: trimmed });
      const modStatus = res.data?.moderation_status;
      const status = res.data?.status;
      if (modStatus === 'pending') {
        toast.success(`Submitted "${trimmed}" — pending admin review`, {
          description: "We'll notify you when it's approved. In the meantime it's visible only to you.",
        });
      } else if (status === 'pinned') {
        toast.success(`Pinned "${trimmed}"`);
      } else {
        toast.success(`Created and pinned "${trimmed}"`);
      }
      setQuery('');
      setResults([]);
      setShowResults(false);
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create tag';
      toast.error(msg);
    } finally {
      setBusyTagId(null);
    }
  };

  const unpinTag = async (tag_id, name) => {
    setBusyTagId(tag_id);
    try {
      await axios.delete(`${API_URL}/api/favorites/tags/${encodeURIComponent(tag_id)}`);
      toast.success(`Removed "${name}" from your interests`);
      await load();
    } catch (err) {
      toast.error('Failed to remove tag');
    } finally {
      setBusyTagId(null);
    }
  };

  // The "create new tag" option in the dropdown — only when query is unique
  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return results.find(r => (r.name || '').toLowerCase() === q) || null;
  }, [query, results]);
  const canCreate = query.trim().length >= 2 && !exactMatch;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="favorite-tags-tab">
      {/* Header description */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 font-rubik" data-testid="fav-tags-blurb">
        Your favorite topics power personalized news on your homepage, daily bill
        discoveries, and the "For you" sort on the Polls page.
      </div>

      {/* Add-tag input */}
      <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="fav-tags-add-card">
        <label className="text-xs uppercase tracking-wide text-gray-500 font-rubik font-semibold flex items-center gap-2 mb-2">
          <MdAdd /> Add a topic to your interests
        </label>
        <div className="relative" ref={searchRef}>
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:border-primary">
            <MdSearch className="text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
              onFocus={() => setShowResults(true)}
              placeholder="Search topics (e.g. Climate, Veterans) or type a new one"
              className="flex-1 bg-transparent outline-none text-sm text-gray-800 font-rubik"
              data-testid="fav-tags-search-input"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setResults([]); }}
                className="text-gray-400 hover:text-gray-700"
                data-testid="fav-tags-clear-search"
                aria-label="Clear"
              >
                <MdClose />
              </button>
            )}
          </div>
          {showResults && (query.trim().length > 0) && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="fav-tags-search-results">
              {searching && (
                <div className="px-3 py-2 text-xs text-gray-500 italic">Searching…</div>
              )}
              {!searching && results.length === 0 && !canCreate && (
                <div className="px-3 py-2 text-xs text-gray-500 italic">No matches.</div>
              )}
              {results.map((r) => {
                const already = manualIdSet.has(r.tag_id);
                return (
                  <button
                    key={r.tag_id}
                    type="button"
                    onClick={() => !already && pinTag(r.tag_id, r.name)}
                    disabled={already || busyTagId === r.tag_id}
                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 text-sm font-rubik transition-colors ${
                      already ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'hover:bg-primary/5 text-gray-800'
                    }`}
                    data-testid={`fav-tags-result-${r.tag_id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.color || '#6B7280' }} />
                      <span className="truncate">{r.name}</span>
                    </span>
                    {already ? (
                      <span className="text-[10px] text-gray-400 uppercase tracking-wide">Pinned</span>
                    ) : (
                      <MdPushPin className="text-gray-400" />
                    )}
                  </button>
                );
              })}
              {canCreate && (
                <button
                  type="button"
                  onClick={() => createAndPin(query)}
                  disabled={busyTagId === '__creating__'}
                  className="w-full text-left px-3 py-2 border-t border-gray-100 bg-amber-50/60 hover:bg-amber-50 text-sm font-rubik text-amber-900 flex items-center justify-between gap-3"
                  data-testid="fav-tags-create-new"
                  title="New topics are visible to you immediately and reviewed by an admin before going platform-wide."
                >
                  <span className="flex items-center gap-2 truncate">
                    <MdAdd /> Suggest new topic &ldquo;<span className="font-semibold">{query.trim()}</span>&rdquo;
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-white border border-amber-300 rounded-full px-2 py-0.5">Needs review</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Inferred section */}
      <section className="bg-white rounded-xl border border-gray-200 p-4" data-testid="fav-tags-inferred-section">
        <h3 className="text-sm font-rubik font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <MdAutoAwesome className="text-amber-500" /> From your favorited polls
        </h3>
        <p className="text-xs text-gray-500 font-rubik mb-3">
          Topics we detected from the polls you&rsquo;ve favorited. Pin one to
          keep it in your interests even if you unfavorite the poll later.
        </p>
        {inferred.length === 0 ? (
          <p className="text-xs text-gray-400 italic" data-testid="fav-tags-inferred-empty">
            Favorite a few polls to see your interest topics show up here.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {inferred.map((t) => (
              <span
                key={t.tag_id}
                className={`inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full text-xs font-rubik border transition-colors ${
                  t.is_pinned ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100'
                }`}
                data-testid={`fav-tags-inferred-chip-${t.tag_id}`}
              >
                <span className="font-medium">{t.name}</span>
                <span className="text-[10px] text-gray-500">{t.poll_count} poll{t.poll_count === 1 ? '' : 's'}</span>
                {t.is_pinned ? (
                  <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold uppercase tracking-wide">Pinned</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => pinTag(t.tag_id, t.name)}
                    disabled={busyTagId === t.tag_id}
                    className="ml-1 px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-700 hover:bg-amber-100 text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1 disabled:opacity-50"
                    data-testid={`fav-tags-pin-${t.tag_id}`}
                  >
                    <MdPushPin className="text-[10px]" /> Pin
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Manual section */}
      <section className="bg-white rounded-xl border border-gray-200 p-4" data-testid="fav-tags-manual-section">
        <h3 className="text-sm font-rubik font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <MdLabel className="text-primary" /> Added by you
        </h3>
        <p className="text-xs text-gray-500 font-rubik mb-3">
          Topics you&rsquo;ve explicitly pinned. These power personalized news
          + the daily bill discovery sweep.
        </p>
        {manual.length === 0 ? (
          <p className="text-xs text-gray-400 italic" data-testid="fav-tags-manual-empty">
            None yet. Pin an inferred topic above, or use the search to add one.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {manual.map((t) => {
              const isPending = t.status === 'pending';
              return (
                <span
                  key={t.tag_id}
                  className={`inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full text-xs font-rubik border ${
                    isPending
                      ? 'bg-amber-50 border-amber-300 text-amber-900'
                      : 'bg-primary/10 border-primary/30 text-primary'
                  }`}
                  data-testid={`fav-tags-manual-chip-${t.tag_id}`}
                >
                  <span className="font-medium">{t.name}</span>
                  {isPending && (
                    <span
                      className="px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[9px] font-semibold uppercase tracking-wide"
                      data-testid={`fav-tags-pending-badge-${t.tag_id}`}
                      title="Awaiting admin review. Only you can see this tag for now."
                    >
                      Pending review
                    </span>
                  )}
                  {!isPending && t.poll_count > 0 && (
                    <span className="text-[10px] text-primary/70">{t.poll_count} poll{t.poll_count === 1 ? '' : 's'}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => unpinTag(t.tag_id, t.name)}
                    disabled={busyTagId === t.tag_id}
                    className={`ml-1 w-5 h-5 inline-flex items-center justify-center rounded-full bg-white hover:bg-red-50 hover:text-red-500 disabled:opacity-50 ${
                      isPending ? 'text-amber-700' : 'text-primary'
                    }`}
                    data-testid={`fav-tags-unpin-${t.tag_id}`}
                    aria-label={`Remove ${t.name}`}
                  >
                    <MdClose className="text-sm" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
